import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { scheduleOnce } from '@ember/runloop';
import { modifier } from 'ember-modifier';
import { PhCheck } from 'ember-phosphor-icons';
import type { WithBoundArgs } from '@glint/template';

type SelectionMode = 'none' | 'single' | 'multiple';
type ListboxType = 'menu' | 'listbox';
type ListboxIntent = 'default' | 'primary' | 'success' | 'warning' | 'danger';
type ListboxAppearance = 'default' | 'outlined' | 'faded';
type AutoActivateMode = 'none' | 'first' | 'selected';

interface RegisteredListItem {
  element: HTMLLIElement;
  key: string;
  textValue: string;
}

interface ListboxSignature {
  Args: {
    type?: ListboxType;
    selectionMode?: SelectionMode;
    selectedKeys?: string[];
    disabledKeys?: string[];
    allowEmpty?: boolean;
    class?: string;
    isKeyboardEventsEnabled?: boolean;
    elementToAddKeyboardEvents?: HTMLElement;
    autoActivateMode?: AutoActivateMode;
    onAction?: (key: string) => void;
    onSelectionChange?: (keys: string[]) => void;
    onActiveItemChange?: (key?: string) => void;
    appearance?: ListboxAppearance;
    intent?: ListboxIntent;
  };
  Element: HTMLUListElement;
  Blocks: {
    default: [
      {
        Item: WithBoundArgs<typeof ListboxItem, 'manager' | 'appearance' | 'intent' | 'type'>;
        Separator: typeof ListboxSeparator;
      },
    ];
  };
}

interface ListboxItemSignature {
  Args: {
    manager: ListboxManager;
    key: string;
    textValue?: string;
    description?: string;
    shortcut?: string;
    onClick?: () => void;
    class?: string;
    withDivider?: boolean;
    appearance?: ListboxAppearance;
    intent?: ListboxIntent;
    type?: ListboxType;
  };
  Element: HTMLLIElement;
  Blocks: {
    default: [];
    selectedIcon: [];
    start: [];
    end: [];
  };
}

interface ListboxSeparatorSignature {
  Args: {
    class?: string;
  };
  Element: HTMLLIElement;
  Blocks: {
    default: [];
  };
}

const listboxClasses = 'min-w-40 rounded-md p-1 outline-none';
const separatorClasses = 'my-1 border-t tio-border-default';
let nextListboxId = 0;

const intentClasses: Record<
  ListboxIntent,
  {
    active: string;
    selected: string;
    selectedIcon: string;
    text: string;
  }
> = {
  default: {
    active: 'tio-listbox-item--default-active',
    selected: 'tio-listbox-item--default-selected',
    selectedIcon: 'tio-listbox-item__selected-icon--default',
    text: 'tio-listbox-item--default',
  },
  primary: {
    active: 'tio-listbox-item--primary-active',
    selected: 'tio-listbox-item--primary-selected',
    selectedIcon: 'tio-listbox-item__selected-icon--primary',
    text: 'tio-listbox-item--primary',
  },
  success: {
    active: 'tio-listbox-item--success-active',
    selected: 'tio-listbox-item--success-selected',
    selectedIcon: 'tio-listbox-item__selected-icon--success',
    text: 'tio-listbox-item--success',
  },
  warning: {
    active: 'tio-listbox-item--warning-active',
    selected: 'tio-listbox-item--warning-selected',
    selectedIcon: 'tio-listbox-item__selected-icon--warning',
    text: 'tio-listbox-item--warning',
  },
  danger: {
    active: 'tio-listbox-item--danger-active',
    selected: 'tio-listbox-item--danger-selected',
    selectedIcon: 'tio-listbox-item__selected-icon--danger',
    text: 'tio-listbox-item--danger',
  },
};

function compactClasses(classes: Array<string | false | undefined>) {
  return classes.filter((className): className is string => Boolean(className)).join(' ');
}

function isTextInputTarget(target: EventTarget | null) {
  return target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement;
}

function idSafeKey(key: string) {
  return encodeURIComponent(key).replace(/[^A-Za-z0-9_-]/g, '-');
}

class ListboxManager {
  @tracked activeKey?: string;

  private listboxId = `tio-listbox-${nextListboxId++}`;
  private searchKeys = '';
  private searchTimer?: number;
  private items: RegisteredListItem[] = [];

  constructor(private owner: Listbox) {}

  get type() {
    return this.owner.type;
  }

  get intent() {
    return this.owner.args.intent ?? 'default';
  }

  get appearance() {
    return this.owner.args.appearance ?? 'default';
  }

  get activeItemId() {
    return this.activeKey ? this.itemId(this.activeKey) : undefined;
  }

  itemId(key: string) {
    return `${this.listboxId}-item-${idSafeKey(key)}`;
  }

  register(element: HTMLLIElement, key: string, textValue: string) {
    this.items = [
      ...this.items.filter((item) => item.element !== element),
      { element, key, textValue },
    ];
    // eslint-disable-next-line ember/no-runloop -- coalesce DOM registrations into a single afterRender sync
    scheduleOnce('afterRender', this, this.syncActiveItem);
  }

  unregister(element: HTMLLIElement) {
    this.items = this.items.filter((registeredItem) => registeredItem.element !== element);
    // eslint-disable-next-line ember/no-runloop -- coalesce DOM unregistrations into a single afterRender sync
    scheduleOnce('afterRender', this, this.syncActiveItem);
  }

  isDisabled(key: string) {
    return this.owner.args.disabledKeys?.includes(key) ?? false;
  }

  isSelected(key: string) {
    return this.owner.args.selectedKeys?.includes(key) ?? false;
  }

  isActive(key: string) {
    return this.activeKey === key;
  }

  select(key: string) {
    if (this.isDisabled(key)) {
      return;
    }

    this.activateKey(key);

    if (this.selectionMode !== 'none') {
      this.owner.args.onSelectionChange?.(this.nextSelectedKeys(key));
    }

    this.owner.args.onAction?.(key);
  }

  activateNext() {
    this.activateByOffset(1);
  }

  activatePrevious() {
    this.activateByOffset(-1);
  }

  activateFirst() {
    this.activateItem(this.enabledItems[0]);
  }

  /** Moves the highlight to the item under the pointer, without scrolling the list beneath it. */
  activateHovered(key: string) {
    if (this.isDisabled(key) || this.activeKey === key) {
      return;
    }

    this.activeKey = key;
    this.owner.args.onActiveItemChange?.(key);
  }

  activateLast() {
    this.activateItem(this.enabledItems[this.enabledItems.length - 1]);
  }

  selectActive() {
    if (!this.activeKey) {
      return;
    }

    this.select(this.activeKey);
  }

  search(key: string) {
    window.clearTimeout(this.searchTimer);
    this.searchKeys = `${this.searchKeys}${key.toLowerCase()}`;
    this.searchTimer = window.setTimeout(() => {
      this.searchKeys = '';
    }, 500);

    const match = this.enabledItems.find((item) =>
      item.textValue.trim().toLowerCase().startsWith(this.searchKeys)
    );
    this.activateItem(match);
  }

  private get selectionMode() {
    return this.owner.args.selectionMode ?? 'none';
  }

  private get autoActivateMode() {
    return this.owner.args.autoActivateMode ?? 'first';
  }

  private get enabledItems() {
    return this.items.filter((item) => !this.isDisabled(item.key));
  }

  private syncActiveItem() {
    const enabledItems = this.enabledItems;
    if (this.activeKey && enabledItems.some((item) => item.key === this.activeKey)) {
      return;
    }

    this.activeKey = undefined;
    if (this.autoActivateMode === 'none') {
      return;
    }

    if (this.autoActivateMode === 'selected') {
      const selectedItem = enabledItems.find((item) => this.isSelected(item.key));
      if (selectedItem) {
        this.activateItem(selectedItem);
        return;
      }
    }

    this.activateItem(enabledItems[0]);
  }

  private activateKey(key: string) {
    this.activateItem(this.enabledItems.find((item) => item.key === key));
  }

  private activateByOffset(offset: number) {
    const items = this.enabledItems;
    if (items.length === 0) {
      return;
    }

    const currentIndex = items.findIndex((item) => item.key === this.activeKey);
    const nextIndex =
      currentIndex === -1 ? 0 : (currentIndex + offset + items.length) % items.length;
    this.activateItem(items[nextIndex]);
  }

  private activateItem(item?: RegisteredListItem) {
    if (!item || this.activeKey === item.key) {
      return;
    }

    this.activeKey = item.key;
    this.owner.args.onActiveItemChange?.(item.key);
    item.element.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  private nextSelectedKeys(key: string) {
    const selectedKeys = this.owner.args.selectedKeys ?? [];
    const isSelected = selectedKeys.includes(key);

    if (this.selectionMode === 'multiple') {
      if (isSelected) {
        if (!this.owner.args.allowEmpty && selectedKeys.length === 1) {
          return selectedKeys;
        }
        return selectedKeys.filter((selectedKey) => selectedKey !== key);
      }
      return [...selectedKeys, key];
    }

    if (isSelected && this.owner.args.allowEmpty) {
      return [];
    }

    return [key];
  }
}

class ListboxItem extends Component<ListboxItemSignature> {
  private get key() {
    return this.args.key;
  }

  private get textValue() {
    return this.args.textValue ?? this.key;
  }

  get isDisabled() {
    return this.args.manager.isDisabled(this.key);
  }

  get isSelected() {
    return this.args.manager.isSelected(this.key);
  }

  get isActive() {
    return this.args.manager.isActive(this.key);
  }

  get role() {
    return this.args.type === 'menu' ? 'menuitem' : 'option';
  }

  get ariaSelected() {
    if (this.role === 'menuitem') {
      return undefined;
    }

    return this.isSelected ? 'true' : 'false';
  }

  get id() {
    return this.args.manager.itemId(this.key);
  }

  get tabindex() {
    return -1;
  }

  get classNames() {
    const intent = this.args.intent ?? this.args.manager.intent;
    const appearance = this.args.appearance ?? this.args.manager.appearance;
    const classes = intentClasses[intent];

    return compactClasses([
      'relative flex min-h-9 w-full cursor-pointer select-none items-center gap-2 rounded-md px-3 py-2 text-left text-sm outline-none transition-colors',
      appearance === 'outlined' && 'border tio-border-default',
      appearance === 'faded' && 'tio-bg-sunken',
      this.isSelected ? classes.selected : classes.text,
      this.isActive && classes.active,
      this.isDisabled && 'pointer-events-none cursor-not-allowed opacity-50',
      this.args.class,
    ]);
  }

  get labelClasses() {
    return 'tio-fill-min truncate';
  }

  get descriptionClasses() {
    return 'mt-0.5 block text-xs tio-fg-muted';
  }

  get shortcutClasses() {
    return 'ml-auto rounded tio-bg-sunken px-1.5 py-0.5 tio-text-micro font-medium tio-fg-muted';
  }

  get selectedIconClasses() {
    const intent = this.args.intent ?? this.args.manager.intent;
    return compactClasses([
      'ml-auto flex size-4 shrink-0 items-center justify-center',
      intentClasses[intent].selectedIcon,
    ]);
  }

  registerItem = modifier((element: HTMLLIElement) => {
    this.args.manager.register(element, this.key, this.textValue);
    return () => {
      this.args.manager.unregister(element);
    };
  });

  select = () => {
    if (this.isDisabled) {
      return;
    }

    this.args.manager.select(this.key);
    this.args.onClick?.();
  };

  activate = () => {
    this.args.manager.activateHovered(this.key);
  };

  <template>
    {{! template-lint-disable no-positive-tabindex }}
    {{! this.tabindex is always -1 (roving tabindex); rule can't statically verify the dynamic binding }}
    <li
      {{this.registerItem}}
      {{on "click" this.select}}
      {{on "pointermove" this.activate}}
      id={{this.id}}
      role={{this.role}}
      aria-selected={{this.ariaSelected}}
      aria-disabled={{if this.isDisabled "true" "false"}}
      tabindex={{this.tabindex}}
      data-active={{if this.isActive "true" "false"}}
      data-selected={{if this.isSelected "true" "false"}}
      data-disabled={{if this.isDisabled "true" "false"}}
      data-test-id="listbox-item"
      data-component="listbox-item"
      data-key={{this.key}}
      class={{this.classNames}}
      ...attributes
    >
      {{yield to="start"}}

      {{#if @description}}
        <span class="tio-fill-min">
          <span data-test-id="listbox-item-label" class={{this.labelClasses}}>{{yield}}</span>
          <span data-test-id="listbox-item-description" class={{this.descriptionClasses}}>
            {{@description}}
          </span>
        </span>
      {{else}}
        <span data-test-id="listbox-item-label" class={{this.labelClasses}}>{{yield}}</span>
      {{/if}}

      {{#if @shortcut}}
        <kbd data-test-id="listbox-item-shortcut" class={{this.shortcutClasses}}>{{@shortcut}}</kbd>
      {{/if}}

      {{#if this.isSelected}}
        <span data-test-id="listbox-item-selected-icon" class={{this.selectedIconClasses}}>
          {{#if (has-block "selectedIcon")}}
            {{yield to="selectedIcon"}}
          {{else}}
            <PhCheck @size={{14}} @weight="bold" />
          {{/if}}
        </span>
      {{/if}}

      {{yield to="end"}}
    </li>
    {{#if @withDivider}}
      <ListboxSeparator />
    {{/if}}
  </template>
}

class ListboxSeparator extends Component<ListboxSeparatorSignature> {
  get classNames() {
    return compactClasses([separatorClasses, this.args.class]);
  }

  <template>
    <li
      role="separator"
      data-test-id="listbox-separator"
      data-component="listbox-separator"
      class={{this.classNames}}
      ...attributes
    >
      {{yield}}
    </li>
  </template>
}

export class Listbox extends Component<ListboxSignature> {
  manager = new ListboxManager(this);

  get type() {
    return this.args.type ?? (this.args.selectionMode ? 'listbox' : 'menu');
  }

  get classNames() {
    return compactClasses([listboxClasses, this.args.class]);
  }

  get isKeyboardEventsEnabled() {
    return this.args.isKeyboardEventsEnabled !== false;
  }

  get ariaMultiselectable() {
    if (this.type === 'listbox' && this.args.selectionMode === 'multiple') {
      return 'true';
    }

    return undefined;
  }

  handleKeyDown = (event: KeyboardEvent) => {
    if (!this.isKeyboardEventsEnabled || isTextInputTarget(event.target)) {
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.manager.activateNext();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.manager.activatePrevious();
        break;
      case 'Home':
      case 'PageUp':
        event.preventDefault();
        this.manager.activateFirst();
        break;
      case 'End':
      case 'PageDown':
        event.preventDefault();
        this.manager.activateLast();
        break;
      case 'Enter':
      case ' ':
      case 'Space':
        event.preventDefault();
        this.manager.selectActive();
        break;
      default:
        if (event.key.length === 1 && !event.altKey && !event.ctrlKey && !event.metaKey) {
          this.manager.search(event.key);
        }
    }
  };

  setupExternalKeyboardEvents = modifier(
    (_element: HTMLUListElement, _positional: unknown[], named: { element?: HTMLElement }) => {
      const element = named.element;
      if (!element) {
        return;
      }

      element.addEventListener('keydown', this.handleKeyDown);
      return () => {
        element.removeEventListener('keydown', this.handleKeyDown);
      };
    }
  );

  <template>
    <ul
      role={{this.type}}
      tabindex="0"
      aria-activedescendant={{this.manager.activeItemId}}
      aria-multiselectable={{this.ariaMultiselectable}}
      {{on "keydown" this.handleKeyDown}}
      {{this.setupExternalKeyboardEvents element=@elementToAddKeyboardEvents}}
      data-test-id="listbox"
      data-component="listbox"
      class={{this.classNames}}
      ...attributes
    >
      {{yield
        (hash
          Item=(component
            ListboxItem manager=this.manager appearance=@appearance intent=@intent type=this.type
          )
          Separator=ListboxSeparator
        )
      }}
    </ul>
  </template>
}

export { ListboxItem, ListboxSeparator };
export type {
  AutoActivateMode,
  ListboxAppearance,
  ListboxIntent,
  ListboxItemSignature,
  ListboxSeparatorSignature,
  ListboxSignature,
  ListboxType,
  SelectionMode,
};
export default Listbox;
